import { useState } from "react";
import { BadgeCheck, HelpCircle } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { useCloseOnScroll } from "@/hooks/useCloseOnScroll";

type Size = "sm" | "md";

interface VerificationBadgeProps {
  verified: boolean;
  size?: Size;
}

/**
 * Badge weryfikacji ze "stuknij/kliknij, aby dowiedzieć się więcej".
 * Używa Popover (klik) zamiast Tooltip - działa zarówno na mobile (tap)
 * jak i na desktop. Zamyka się przy scrollu.
 */
export const VerificationBadge = ({ verified, size = "sm" }: VerificationBadgeProps) => {
  const [open, setOpen] = useState(false);
  useCloseOnScroll(open, () => setOpen(false));

  const sizeClasses =
    size === "md"
      ? "text-[11px] px-2 py-0.5 gap-1"
      : "text-[10px] px-1.5 py-0 leading-4 gap-1";
  const iconSize = size === "md" ? "w-3 h-3" : "w-2.5 h-2.5";

  const baseClasses = `inline-flex items-center font-medium rounded-full border cursor-pointer ${sizeClasses}`;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          onClick={(e) => e.stopPropagation()}
          aria-label={verified ? "Co oznacza Zweryfikowane?" : "Co oznacza Niezweryfikowane?"}
          className={
            verified
              ? `${baseClasses} bg-primary/10 text-primary border-primary/20 hover:bg-primary/15`
              : `${baseClasses} bg-muted text-muted-foreground border-border hover:bg-muted/80`
          }
        >
          {verified ? (
            <>
              <BadgeCheck className={iconSize} />
              {size === "md" ? "Zweryfikowane przez organizację" : "Zweryfikowane"}
            </>
          ) : (
            <>
              <HelpCircle className={iconSize} />
              Niezweryfikowane
            </>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent
        side="top"
        align="end"
        sideOffset={8}
        collisionPadding={16}
        sticky="always"
        avoidCollisions
        className="w-[240px] text-xs leading-relaxed p-3 z-[2200]"
        onClick={(e) => e.stopPropagation()}
      >
        {verified ? (
          <>
            <p className="font-semibold text-foreground mb-1">Profil zweryfikowany</p>
            <p className="text-muted-foreground">
              Dane potwierdzone przez organizację lub profil prowadzony przez schronisko.
            </p>
          </>
        ) : (
          <>
            <p className="font-semibold text-foreground mb-1">Profil niezweryfikowany</p>
            <p className="text-muted-foreground">
              Dane z internetu, jeszcze niepotwierdzone. Masz aktualne info? Otwórz profil i zgłoś aktualizację.
            </p>
          </>
        )}
      </PopoverContent>
    </Popover>
  );
};
